import React, { CSSProperties, useEffect, useId, useState } from "react";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  DragEndEvent,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import "./customTable.scss";
import NofoundLoader from "../../../widgets/nofound";
import ReactPaginate from "react-paginate";
import dragIcon from "../../../images/drag.png";
import "../customTable/customTable.scss";

const RowDragHandleCell = ({ rowId ,isDraggable }: { rowId: any;isDraggable: any }) => {
  const { attributes, listeners } = useSortable({ id: rowId?.id });
  const [imageError, setImageError] = useState(false);

  const handleImageError = () => {
    setImageError(true);
  };

  return (
    <div {...attributes} {...listeners} className="drag-handle">
      {imageError ? (
        <span className="material-symbols-outlined">drag_indicator</span>
      ) : (
        <img
          src={dragIcon}
          alt="drag icon"
          onError={handleImageError}
          className="drag-class"
        />
      )}
    </div>
  );
};


const DraggableRow = ({ row }: any) => {
  const { transform, transition, setNodeRef, isDragging } = useSortable({
    id: row.id,
   // disabled: !row.original.isDraggable, // Disable if not draggable
  });
  const [uniqueId] = useId();
  const style: CSSProperties = {
    transform: transform ? CSS.Transform.toString(transform) : "none",
    transition: transition,
    opacity: isDragging ? 0.8 : 1,
    zIndex: isDragging ? 1 : 0,
    position: "relative",
    // pointerEvents: row.original.isDraggable ? "auto" : "none",
    backgroundColor: row.original.isDraggable ? "transparent" : "rgba(236, 240, 241, 0.5)",
  };

  return (
    <tr ref={setNodeRef} style={style} className="tr">
      {row.getVisibleCells().map((cell: any, index: any) => (
        <td
          key={`${uniqueId}_${index}`}
          className="td"
          style={{
            width: index === 0 || index === 1 ? "10px" : cell.column.getSize(),
          }}
        >
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </td>
      ))}
    </tr>
  );
};

const DraggableComponent = ({
  columns,
  data,
  setData,
  showPagination,
  disableTable,
  journalPrefix,   // Added journalPrefix prop
  issueNumber,     // Added issueNumber prop
  editionInfo     // Added editionInfo prop
}: any) => {
  const [dataIds, setDataIds] = useState<any[]>([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 50 });
  const [pageData, setPageData] = useState<any[]>([]);

  useEffect(() => {
    const newDataIds = data.map(({ id }: any) => id);
    setDataIds(newDataIds);

    const start = pagination.pageIndex * pagination.pageSize;
    const end = start + pagination.pageSize;
    setPageData(data.slice(start, end));
  }, [data, pagination]);

  const table = useReactTable({
    data: pageData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row: any) => row.id,
    debugTable: true,
    debugHeaders: true,
    debugColumns: true,
  });

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
  
    if (!active || !over || active.id === over.id) return;
  
    const overRow = data.find((row: any) => row.id === over.id);
    //if (!overRow?.isDraggable) return; // Prevent dropping if overRow is not draggable
  
    setData((prevData: any) => {
      const oldIndex = prevData.findIndex((item: any) => item.id === active.id);
      const newIndex = prevData.findIndex((item: any) => item.id === over.id);
  
      const newOrderedData = arrayMove(prevData, oldIndex, newIndex);
      const movedArticles = newOrderedData.filter((item: any) => item.is_moved_to_production === 'Yes');
      const movedArticlesCount = movedArticles.length;
      const maxMovedSequence = Math.max(
        0,
        ...newOrderedData
          .filter((r:any) => r.is_moved_to_production === 'Yes')
          .map((r:any) => Number(r.newsequence || 0))
      );
         // Recalculate sequence order and articlename properly for unmoved articles
    const updatedData = newOrderedData.map((item: any, index: any) => {
     if(item){
        const newSequence = index + 1;
        let sequenceToUse: number;
      
       
          // Not moved → need to find unmoved order
          const unmovedArticles = newOrderedData.filter((r:any) => r.is_moved_to_production !== 'Yes');
          const unmovedIndex = unmovedArticles.findIndex((r:any) => r.id === item.id);
          console.log(unmovedIndex,maxMovedSequence,"unmovedIndex")
          sequenceToUse = movedArticlesCount + unmovedIndex + 1;
        
    
        const indexFormatted = sequenceToUse.toString().padStart(2, "0");
        // Generate articlename based on sequence
        const articlename = item.is_moved_to_production !== 'Yes'? `${journalPrefix}_${issueNumber ?? "defaultNumber"}_${editionInfo ?? "defaultEdition"}${indexFormatted.toString().padStart(2, "0")}`:item.articletitlename;

        return { 
          ...item, 
          sequenceorder: newSequence, // Update sequence
          articletitlename: articlename, // Update articlename
        };
     }
      return item; // Keep moved articles' articlename and sequence unchanged
    });

    return updatedData;
  });
}
  

  const onPageChange = ({ selected }: { selected: number }) => {
    setPagination({ ...pagination, pageIndex: selected });
  };

  const pageCount = Math.ceil(data.length / pagination.pageSize);
  const pageIndex = pagination.pageIndex + 1;

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {})
  );

  const headerRowMinWidth = (columnKey: any) => {
    switch (columnKey) {
      case "drag":
        return "10px";
      case "checkbox":
        return "10px";
      case "sequence":
        return "10px";
      case "action":
        return "200px";
      default:
        return "150px";
    }
  };

  const disable: any = {
    // pointerEvents: disableTable ? "none" : "auto",
    // backgroundColor: disableTable ? "rgba(236, 240, 241, 0.5)" : "transparent"
  };

  return (
    <DndContext
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis]}
      onDragEnd={handleDragEnd}
      sensors={sensors}
    >
      <div className="draggable-container" style={{ paddingTop: "15px", overflow: "auto" }}>
        <table className="w-100" style={disable}>
          <thead className="thead">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="tr">
                {headerGroup.headers.map((header, index) => (
                  <th
                    key={header.id}
                    className="th"
                    colSpan={header.colSpan}
                    style={{
                      width: index === 0 || index === 1 ? "10px" : headerRowMinWidth(header.id),
                      minWidth: index === 0 || index === 1 ? "auto" : headerRowMinWidth(header.id),
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {pageData.length === 0 ? (
              <tr>
                <td className="no-data-found td" colSpan={columns.length}>
                  <NofoundLoader />
                </td>
              </tr>
            ) : (
              <React.Fragment>
               <SortableContext items={data.map((row: any) => row.id)} strategy={verticalListSortingStrategy}>
                  {table.getRowModel().rows.map((row: any) => (
                    <DraggableRow key={row.id} row={row} />
                  ))}
                </SortableContext>
              </React.Fragment>
            )}
          </tbody>
        </table>
      </div>
      {showPagination && (
        <div
          className="mt-4 pagination"
          style={{ display: "flex", justifyContent: "space-between" }}
        >
          <div
            style={{
              marginRight: "15px",
              marginLeft: "8px",
              fontSize: "14px",
              padding: 0,
            }}
          >
            <span className="f-14">Showing </span>
            <span>
              {pageIndex} of {pageCount}
            </span>
            {" - "}
            <span>{data.length}</span>
            <span className="f-14"> entries</span>
          </div>
          <ReactPaginate
            previousLabel={
              <span className="material-icons action-arrow-icon">
                chevron_left
              </span>
            }
            nextLabel={
              <span className="material-icons action-arrow-icon">
                chevron_right
              </span>
            }
            breakLabel={"..."}
            breakClassName={"break-me"}
            pageCount={pageCount}
            marginPagesDisplayed={2}
            pageRangeDisplayed={5}
            onPageChange={onPageChange}
            containerClassName={"pagination"}
            activeClassName={"active"}
            previousLinkClassName={"previous"}
            nextLinkClassName={"next"}
          />
        </div>
      )}
    </DndContext>
  );
};

export default DraggableComponent;
export { RowDragHandleCell };
